// Programme settings and Hub service areas (Phase 3). Every type and path of these APIs lives in this one file,
// aligned with the backend's src/modules/program-settings (DTOs, program-settings.types.ts) and
// common/rules/household-order.rules.ts › SETTING_RULES.
import { send } from './client';
import { query, type Cursor, type CursorPage } from './paging';

/** The keys the dashboard knows how to explain (program_setting.key); an unknown one is still shown and saved. */
export const PROGRAM_SETTING_KEYS = [
  'QUOTA_PER_MONTH', 'MAX_HELD_PER_HOUSEHOLD', 'MAX_CANCELLATIONS_PER_MONTH',
  'PRICE', 'DELIVERY_FEE', 'DEPOSIT', 'FINE_DAMAGED', 'FINE_LOST',
  'ORDER_SLA_HOURS', 'CONFIRMATION_ATTEMPTS', 'GPS_DISTANCE_MAX_M',
  'HANDOVER_GRACE_MINUTES', 'TAG_DEAD_WINDOW_HOURS', 'EMPTY_RETURN_HOURS', 'COURIER_MISMATCH_THRESHOLD', 'DORMANT_DAYS', 'RETEST_GRACE_DAYS',
  'HUB_SEARCH_RADIUS_KM',
] as const;
export type ProgramSettingKey = (typeof PROGRAM_SETTING_KEYS)[number];

/** One setting as GET /program-settings answers it: the value in force, its default and bounds. */
export interface ProgramSettingEntry {
  key: ProgramSettingKey | string;
  value: number;
  default: number;
  min: number;
  max: number;
  /** a placeholder the national admin must replace before launch (the price, the fees, the fines) */
  placeholder: boolean;
  /** never set: the default is in force */
  isDefault: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}
/** GET /program-settings: the settings and the version a save sends back (409 STALE_VERSION when someone saved meanwhile). */
export interface ProgramSettingsState { settings: ProgramSettingEntry[]; version: number }
/** One change (GET /program-settings/history, newest first). */
export interface ProgramSettingChange { id: number; key: ProgramSettingKey | string; oldValue: number | null; newValue: number; changedByName: string | null; changedAt: string; note: string | null }
export type ProgramSettingHistoryPage = CursorPage<ProgramSettingChange>;

export const programSettingPaths = {
  current: '/program-settings',
  history: (cursor: Cursor, limit = 20) => `/program-settings/history${query({ ...cursor, limit })}`,
};
/**
 * PUT /program-settings {values, note}: only the keys that change, and why. New values apply to orders placed from
 * now on; open orders keep the amounts of when they were placed.
 */
export const saveProgramSettings = (body: { version: number; values: Partial<Record<string, number>>; note: string }) =>
  send<WireSettings>(programSettingPaths.current, { version: body.version, values: body.values, ...(body.note ? { note: body.note } : {}) }, 'PUT').then(toSettingsState);

// --- the backend's answers (program-settings.service.ts › SettingView, history) ------------------------------------
export interface WireSetting { key: string; label: string; value: number; default: number; min: number; max: number; placeholder: boolean; isDefault: boolean; updatedAt: string | null; updatedBy: { id: number; name: string } | null }
export interface WireSettingChange { id: number; key: string; label: string; oldValue: number | null; newValue: number; changedAt: string; note: string | null; changedBy: { id: number; name: string } | null }
export interface WireSettings { version: number; settings: WireSetting[] }
export const toSettingsState = (w: WireSettings): ProgramSettingsState => ({
  settings: w.settings.map(({ updatedBy, label: _label, ...s }) => ({ ...s, updatedByName: updatedBy?.name ?? null })),
  version: w.version,
});
export const toSettingHistory = (p: CursorPage<WireSettingChange>): ProgramSettingHistoryPage => ({ items: p.items.map(({ changedBy, label: _l, ...c }) => ({ ...c, changedByName: changedBy?.name ?? null })), meta: p.meta });

// --- a Hub's service area ---------------------------------------------------------------------------------------------

export interface ServiceVillage { code: string; name: string; districtName: string; regencyName: string; /** the Hub's own village (always served) */ own: boolean }
/** The screen's model of a service area (the backend answers no "updated by" yet: null). */
export interface ServiceArea { locationId: number; villages: ServiceVillage[]; updatedAt: string | null; updatedByName: string | null }
/** GET /locations/:id/service-area (service-areas.service.ts) */
export interface WireServiceArea { locationId: number; ownVillage: { code: string; name: string } | null; villages: { code: string; name: string; districtName: string }[] }
export const toServiceArea = (w: WireServiceArea): ServiceArea => ({
  locationId: w.locationId,
  villages: [
    ...(w.ownVillage ? [{ code: w.ownVillage.code, name: w.ownVillage.name, districtName: '', regencyName: '', own: true }] : []),
    ...w.villages.filter((v) => v.code !== w.ownVillage?.code).map((v) => ({ ...v, regencyName: '', own: false })),
  ],
  updatedAt: null,
  updatedByName: null,
});
export const serviceAreaPath = (locationId: number) => `/locations/${locationId}/service-area`;
/** PUT /locations/:id/service-area {villageCodes}: the whole set besides the Hub's own (always in). */
export const saveServiceArea = (locationId: number, villageCodes: string[]) => send<WireServiceArea>(serviceAreaPath(locationId), { villageCodes }, 'PUT').then(toServiceArea);
